import React from 'react';
import type { ReactNode } from 'react';
import { SubmitButton } from '../SubmitButton';
import Button from '../Button';

type Step = {
  label: string;
  content: ReactNode;
};

type StepperProps = {
  steps: Step[];
  currentStep: number;
  onComplete?: () => void;
  onStepChange: (step: number) => void;
};

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  onComplete,
  onStepChange,
}) => {
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = (
    e?: React.FormEvent<HTMLFormElement> | React.MouseEvent
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!e || e.type === 'submit' || e.type === 'click') {
      if (isLastStep) {
        onComplete?.();
      } else {
        onStepChange(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    onStepChange(currentStep - 1);
  };

  return (
    <div className="w-full flex flex-col h-full justify-between">
      <div className="w-full mx-auto max-w-5xl p-4">
        <div className="flex justify-between items-center mb-8 relative">
          {steps.map((step, index) => {
            const isActive = index === currentStep;
            const isLast = index === steps.length - 1;
            const isFilled = index < currentStep;

            return (
              <div
                key={index}
                className={`flex items-center ${isLast ? '' : 'flex-1'} relative`}
              >
                <div className="flex flex-col items-center">
                  <div
                    className={` ${isActive || isFilled ? 'w-8 h-8' : 'w-6 h-6'} flex items-center justify-center rounded-full border-2 
                      ${isActive || isFilled ? 'border-purple-500 bg-purple-500' : 'border-gray-300 bg-white'}`}
                  >
                    {isActive ||
                      (isFilled && (
                        <div className="w-4 h-4 bg-white rounded-full" />
                      ))}
                  </div>
                </div>
                <div
                  className={`mt-6 text-center text-tiny absolute top-4 ${index === 2 ? '-left-28' : '-left-12'} w-max  ${
                    isActive || isFilled
                      ? 'text-purple-600 font-semibold'
                      : 'text-gray-800'
                  }`}
                >
                  {step.label}
                </div>

                {!isLast ? (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${isFilled ? 'bg-purple-500' : 'bg-gray-200'}`}
                  ></div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="flex flex-col mb-6 min-h-[200px]">
          {steps[currentStep].content}
        </div>
      </div>

      <div className="border-t border-border px-4 py-3 shrink-0">
        <div
          className={`mx-auto flex ${currentStep > 0 ? 'justify-between' : 'justify-end'} items-center max-w-5xl`}
        >
          {currentStep > 0 && (
            <Button
              type="contrast"
              className="px-4 py-2 rounded hover:bg-gray-400 transition"
              label="Back"
              onClick={handleBack}
            />
          )}
          <div className="p-2 ">
            <SubmitButton
              size="medium"
              className="w-full"
              label={isLastStep ? 'Activate' : 'Next'}
              onClick={handleNext}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
